<template>
  <aside
    ref="sidebar"
    data-cy="sidebar__container"
    class="flex flex-col h-full min-h-screen p-6 bg-green-600"
  >
    <section
      ref="sidebar-title"
      data-cy="sidebar__header"
      class="flex gap-2"
    >
      <img
        src="@/assets/icons/logo.svg"
        alt="Portal Jabar Logo"
        width="34px"
        height="34px"
        class="self-start"
      >
      <div>
        <img
          src="@/assets/icons/logo-text.svg"
          alt="Portal Jabar Logo"
          width="75px"
          height="38px"
          class="mb-1"
        >
        <h1
          data-cy="sidebar__header-title"
          class="text-sm font-bold leading-5 text-white font-lato whitespace-nowrap"
        >
          CONTENT MANAGEMENT<br>
          SYSTEM
        </h1>
      </div>
    </section>
    <nav class="sidebar__navigation-container flex-grow pt-10 overflow-y-auto mt-10">
      <ul
        ref="sidebar-navigation"
        data-cy="sidebar__navigation"
        class="w-full"
      >
        <template v-for="navigation in navigationMenu">
          <li
            v-if="isAllowedToAccess(navigation.permission)"
            :key="navigation.label"
            class="w-full mb-2"
            :class="{ 'flex flex-col': navigation.hasChildren }"
          >
            <div
              v-if="navigation.hasChildren"
              class="flex items-center w-full gap-3 whitespace-nowrap rounded-lg cursor-pointer min-h-[50px] p-[15px] hover:bg-green-700 mb-2"
              @click="toggleSubmenu(navigation.link)"
            >
              <!-- Menu Icon -->
              <component
                :is="navigation.icon"
                class="w-5 h-5 fill-white"
              />
              <span class="text-white text-sm font-bold">
                {{ navigation.label }}
              </span>
              <span
                v-if="navigation.arrow"
                class="ml-auto"
              >
                <JdsIcon
                  fill="#fff"
                  :name="
                    openSubmenus[navigation.link]
                      ? 'chevron-up'
                      : 'chevron-down'
                  "
                  size="16px"
                />
              </span>
            </div>

            <router-link
              v-else
              v-slot="{ navigate, isActive }"
              :to="navigation.link"
              :exact="navigation.link === '/'"
              custom
            >
              <div
                :class="{
                  'bg-green-700': isActive,
                  'hover:bg-green-700': true,
                }"
                class="flex items-center w-full gap-3 whitespace-nowrap rounded-lg cursor-pointer min-h-[50px] p-[15px]"
                @click="navigate"
              >
                <!-- Menu Icon -->
                <component
                  :is="navigation.icon"
                  class="w-5 h-5 fill-white"
                />
                <span class="text-white text-sm font-bold">
                  {{ navigation.label }}
                </span>
              </div>
            </router-link>

            <ul
              v-if="navigation.hasChildren"
              v-show="openSubmenus[navigation.link]"
              class="border-l border-l-white/50 ml-6 gap-1"
            >
              <router-link
                v-for="child in navigation.children"
                :key="child.label"
                v-slot="{ href, navigate, isActive }"
                :to="child.link"
                custom
              >
                <li
                  v-if="isAllowedToAccess(child.permission)"
                  class="w-fit min-h-[40px] flex items-center rounded-lg font-lato font-bold text-sm text-white hover:bg-green-700 relative mb-2 ml-4"
                  :class="{ 'bg-green-700': isActive }"
                  style="padding: 15px 15px 15px 15px;"
                >
                  <a
                    :href="href"
                    :title="child.label"
                    class="flex items-center w-full gap-3 whitespace-nowrap"
                    @click="navigate"
                  >
                    {{ child.label }}
                  </a>
                </li>
              </router-link>
            </ul>
          </li>
        </template>
      </ul>
    </nav>
    <section class="mt-auto">
      <ul
        ref="sidebar-bottom-nav"
        data-cy="sidebar__bottom-nav"
        class="mt-auto"
      >
        <!-- Create J-Site -->
        <router-link
          v-if="!hasMicrosite && $hasPermission('j-site.site-management.create')"
          v-slot="{ href, navigate }"
          to="/j-site/onboarding"
          data-cy="j-site-create__button-create"
          custom
        >
          <li
            class="w-full min-h-[50px] p-[15px] flex items-center rounded-lg font-lato
            font-bold text-sm text-white hover:bg-green-700 mb-2"
            :class="{'bg-green-700' : $route.path.includes('j-site/tambah')}"
          >
            <a
              :href="href"
              title="Kelola J-Site"
              class="flex items-center w-full gap-3 whitespace-nowrap"
              @click="navigate"
            >
              <MicrositeIcon class="w-5 h-5" />
              Buat J-Site
            </a>
          </li>
        </router-link>

        <!-- Manage J-Site -->
        <router-link
          v-else-if="hasMicrosite && $hasPermission('j-site.site-management.read')"
          v-slot="{ href, navigate, isActive }"
          to="/j-site"
          data-cy="j-site-create__button-manage"
          custom
        >
          <li
            class="w-full min-h-[50px] p-[15px] flex items-center rounded-lg font-lato
          font-bold text-sm text-white hover:bg-green-700 mb-2"
            :class="{'bg-green-700' : isActive}"
          >
            <a
              :href="href"
              title="Kelola J-Site"
              class="flex items-center w-full gap-3 whitespace-nowrap"
              @click="navigate"
            >
              <MicrositeIcon class="w-5 h-5" />
              Kelola J-Site
            </a>
          </li>
        </router-link>

        <!-- General Settings -->
        <router-link
          v-slot="{ href, navigate, isActive }"
          to="/pengaturan"
          custom
        >
          <li
            class="w-full min-h-[50px] p-[15px] flex items-center rounded-lg font-lato
          font-bold text-sm text-white hover:bg-green-700 mb-2"
            :class="{'bg-green-700' : isActive}"
          >
            <a
              :href="href"
              title="Pengaturan Akun"
              class="flex items-center w-full gap-3 whitespace-nowrap"
              @click="navigate"
            >
              <SettingIcon class="w-5 h-5 fill-white" />
              Pengaturan Akun
            </a>
          </li>
        </router-link>
      </ul>
    </section>
  </aside>
</template>

<script>
import { NAVIGATION_MENU } from '@/common/constants';
import { mapGetters } from 'vuex';
import DashboardIcon from '@/assets/icons/dashboard.svg?inline';
import ServiceIcon from '@/assets/icons/layanan.svg?inline';
import AgendaIcon from '@/assets/icons/agenda.svg?inline';
import NewsIcon from '@/assets/icons/berita.svg?inline';
import SettingIcon from '@/assets/icons/setting.svg?inline';
import LandingPageIcon from '@/assets/icons/landing-page.svg?inline';
import MicrositeIcon from '@/assets/icons/microsite.svg?inline';
import GedungSateIcon from '@/assets/icons/gedung-sate.svg?inline';

export default {
  name: 'Sidebar',
  components: {
    DashboardIcon,
    ServiceIcon,
    AgendaIcon,
    NewsIcon,
    SettingIcon,
    LandingPageIcon,
    MicrositeIcon,
    GedungSateIcon,
    // JdsIcon, // Pastikan JdsIcon diimpor jika tidak global
  },
  data() {
    return {
      navigationMenu: NAVIGATION_MENU,
      openSubmenus: {}, // Object untuk menyimpan status buka/tutup submenu
    };
  },
  computed: {
    ...mapGetters('microsite', ['hasMicrosite']),
    permissions() {
      // Asumsi permissions disimpan di Vuex
      return this.$store.getters['auth/permissions'] || [];
    },
  },
  watch: {
    '$route.path': {
      immediate: true,
      handler(newPath) {
        this.navigationMenu.forEach((menu) => {
          if (menu.hasChildren) {
            const isCurrentRouteWithinParentSection = newPath.startsWith(menu.link);
            const isAnyChildActive = menu.children.some((child) => newPath.startsWith(child.link));

            if (isCurrentRouteWithinParentSection || isAnyChildActive) {
              // Jika rute saat ini adalah rute utama induk atau salah satu anaknya, biarkan terbuka
              this.$set(this.openSubmenus, menu.link, true);
            } else {
              // Jika tidak, tutup submenu
              this.$set(this.openSubmenus, menu.link, false);
            }
          }
        });
      },
    },
  },
  methods: {
    isAllowedToAccess(permission) {
      if (!permission || permission === null) {
        return true;
      }

      if (permission && this.$hasPermission(permission)) {
        return true;
      }

      return false;
    },
    toggleSubmenu(link) {
      // Mengubah status buka/tutup submenu
      this.$set(this.openSubmenus, link, !this.openSubmenus[link]);
    },
    isParentActive(parentMenu) {
      // Memeriksa apakah ada rute anak dari menu induk yang sedang aktif
      return parentMenu.children.some((child) => this.$route.path.startsWith(child.link));
    },
  },
};
</script>

<style scoped>
  .sidebar__navigation-container::-webkit-scrollbar {
    width: 4px;
  }

  .sidebar__navigation-container::-webkit-scrollbar-track {
    background-color: none;
  }

  .sidebar__navigation-container::-webkit-scrollbar-thumb {
    background-color: #9bdbb3;
    outline: none;
    border-radius: 6px;
    background-clip: padding-box;
  }
</style>
